import type { IntlShape } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA } from 'constants/offer'
import config from 'constants/config'

const supportedHolidayTypes = [
  'All-inclusive',
  'Family Friendly',
  'Ski',
  'Theme Park',
  'Honeymoon',
  'Adults-only',
  'Beach',
  undefined,
]
export function getMetaTitle({ destinationLandingPageType, place, holidayType, isExperimentOn, intl }: { destinationLandingPageType: DestinationLandingPageType, place: App.Place, holidayType: string, isExperimentOn?: boolean, intl: IntlShape }) {
  const placeName = place?.name ?? ''
  const year = new Date().getFullYear()
  const nextYear = (year + 1).toString().slice(-2)
  const yearRange = `${year}/${nextYear}`
  if (isExperimentOn) {
    return intl.formatMessage({
      id: 'ehjp8xG6UsqlwC9HsRse',
      defaultMessage: 'Top {placeName} Resorts - Exclusive {yearRange} Offers',
      description: 'Experimental meta title for destination resorts landing page',
    }, { placeName, yearRange })
  } else if (destinationLandingPageType === DestinationLandingPageType.Resorts) {
    return intl.formatMessage({
      id: 'hMNsyRy8KkqBWoJCgGqU',
      defaultMessage: '{placeName} Resorts - Best Exclusive Deals for {yearRange}',
      description: 'Meta title for destination resorts landing page on the promotional landing page',
    }, { placeName, yearRange })
  }
  const { title } = getTitleAndSubTitle({ destinationLandingPageType, placeName, holidayType, intl })
  return title
}

export function getMetaDescription({ destinationLandingPageType, place, holidayType, isExperimentOn, intl }: { destinationLandingPageType: DestinationLandingPageType, place?: App.Place, holidayType: string, isExperimentOn?: boolean, intl: IntlShape }) {
  if (isExperimentOn) {
    const canonicalName = place?.canonicalName
    if (canonicalName) {
      return intl.formatMessage({
        id: '1tQTIHCPDqf7VVkwYRZD',
        defaultMessage: 'Discover the best resorts in {location}. Enjoy exclusive offers, top-tier amenities, and unbeatable savings with Luxury Escapes.',
        description: 'Experimental meta description for destination resorts page with location',
      }, { location: canonicalName })
    }
    return intl.formatMessage({
      id: 'fSEo6IXwzFf2ioU5LLwV',
      defaultMessage: 'Discover the best resorts. Enjoy exclusive offers, top-tier amenities, and unbeatable savings with Luxury Escapes.',
      description: 'Experimental meta description for destination resorts page without location',
    })
  }
  return getDescription({ destinationLandingPageType, place, holidayType, intl })
}

export function getDescription({ destinationLandingPageType, place, holidayType, intl }: { destinationLandingPageType: DestinationLandingPageType, place?: App.Place, holidayType: string, intl: IntlShape }) {
  const year = new Date().getFullYear()
  if (destinationLandingPageType === DestinationLandingPageType.HolidayPackages) {
    const placeName = place?.name ?? ''
    const yearRange = `${year}/${year + 1}`
    return intl.formatMessage({
      id: 'J-8wTeSyMA29kb-rrBM5',
      defaultMessage: '{placeName} {holidayType} Holiday Package deals for travel in {yearRange}. Save on luxury hotel + flight bundles. Get exclusive inclusions.',
      description: 'Meta description for holiday packages destination landing page',
    }, {
      placeName: placeName || '',
      holidayType: holidayType || '',
      yearRange,
    }).trim().replace(/ {2,}/g, ' ')
  } else if (destinationLandingPageType === DestinationLandingPageType.Resorts) {
    const canonicalName = place?.canonicalName
    if (canonicalName) {
      return intl.formatMessage({
        id: 'H1C0-C5Q-UDMq7K3gNbx',
        defaultMessage: 'Discover the best resorts in {location}. Enjoy exclusive offers, top-tier amenities, and unbeatable savings with Luxury Escapes.',
        description: 'Meta description for destination resorts page with location',
      }, { location: canonicalName })
    }
    return intl.formatMessage({
      id: 'r30e4Nav54v7cQ27I5iG',
      defaultMessage: 'Discover the best resorts. Enjoy exclusive offers, top-tier amenities, and unbeatable savings with Luxury Escapes.',
      description: 'Meta description for destination resorts page without location',
    })
  } else {
    throw new Error('getDescription:Invalid destinationLandingPageType')
  }
}

export function capitalizeWords(input: string) {
  return input
    .split(' ') // Split by spaces
    .map(word =>
      word
        .split('-') // Split hyphenated words
        .map((part, index) =>
          index === 0 ?
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : // Capitalize only the first part
              part.toLowerCase(), // Keep subsequent parts lowercase
        )
        .join('-'), // Rejoin hyphenated words
    )
    .join(' ') // Rejoin the entire string
}

// e.g. `all+inclusive` -> `All-inclusive`
// '+' is used to represent ' ' in the url
export function decodeFromUrlString(urlString: string) {
  return capitalizeWords(urlString.replace(/\+/g, ' '))
}

export function validateHolidayType(holidayType: string) {
  if (supportedHolidayTypes.includes(holidayType)) {
    return true
  }
  return false
}

export enum DestinationLandingPageType {
  HolidayPackages = 'holiday-packages',
  Resorts = 'resorts',
}

export function getDestinationLandingPageType(path: string): DestinationLandingPageType {
  if (path.startsWith('/:regionCode/holiday-packages/')) {
    return DestinationLandingPageType.HolidayPackages
  } else if (path.startsWith('/:regionCode/resorts/')) {
    return DestinationLandingPageType.Resorts
  } else {
    throw new Error('getDestinationLandingPageType:Invalid path')
  }
}

interface getFiltersProps {
  filters: App.OfferListFilters
  destinationLandingPageType: DestinationLandingPageType
  hasHolidayType: boolean
  urlHolidayType: string
  place: { id?: string }
}

export function getFilters({
  filters,
  destinationLandingPageType,
  hasHolidayType,
  urlHolidayType,
  place,
}: getFiltersProps): App.OfferListFilters {
  const holidayTypes = hasHolidayType ? [decodeFromUrlString(urlHolidayType)] : []
  const destinationId = place?.id ?? ''
  if (destinationLandingPageType === DestinationLandingPageType.HolidayPackages) {
    return {
      ...filters,
      holidayTypes: [...(filters.holidayTypes ?? []), ...holidayTypes],
      destinationId,
      offerTypes: [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK],
    }
  } else if (destinationLandingPageType === DestinationLandingPageType.Resorts) {
    return {
      ...filters,
      destinationId,
      offerTypes: [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK],
    }
  } else {
    throw new Error('getFilters:Invalid destinationLandingPageType')
  }
}

export function getMoreResortsFilters({
  filters,
}: {
  filters: App.OfferListFilters
}): App.OfferListFilters {
  const { holidayTypes, limit, inclusions, ...restFilters } = filters
  return {
    ...restFilters,
    offerTypes: [OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK],
    customerRatingGte: 8,
  }
}

interface getTitleAndSubTitleProps {
  destinationLandingPageType: DestinationLandingPageType
  placeName: string
  holidayType: string
  intl: IntlShape
}
export function getTitleAndSubTitle({ destinationLandingPageType, placeName, holidayType, intl }: getTitleAndSubTitleProps) {
  if (destinationLandingPageType === DestinationLandingPageType.HolidayPackages) {
    const title = intl.formatMessage({
      id: '4oebVjPkESZ1FgjF6hG1',
      defaultMessage: '{placeName} {holidayType} Holiday Packages',
      description: 'Page title for holiday packages destination landing page',
    }, { placeName, holidayType })
    const subtitle = intl.formatMessage({
      id: 't0fe9ddh29kfXwYARTTY',
      defaultMessage: 'Explore our Holiday Package deals in {placeName}',
      description: 'Page subtitle for holiday packages destination landing page',
    }, { placeName })
    return { title, subtitle }
  } else if (destinationLandingPageType === DestinationLandingPageType.Resorts) {
    const title = intl.formatMessage({
      id: 'alfkq-3aTyLd692gG4cu',
      defaultMessage: '{placeName} Resorts',
      description: 'Page title for resorts destination landing page on the promotional landing page',
    }, { placeName })
    const subtitle = intl.formatMessage({
      id: 'OVFhTZfOlDPZDdqLOi4w',
      defaultMessage: 'Explore our Resorts in {placeName}',
      description: 'Page subtitle for resorts destination landing page',
    }, { placeName })
    return { title, subtitle }
  } else {
    throw new Error('getTitleAndSubTitle:Invalid destinationLandingPageType')
  }
}

/**
 * As in the landing page, the doesn't has search params
 * so the search params passed to the filter are not complete. This function will build the search params
 * that work for the filter. (?destinationName=Thailand&destinationId=le_8f85517967795eeef66c225f7883bdcb&adults=2&children=none&pages=1)
 **/
export function buildSearchParamsForFilterPane({
  originalSearch,
  destinationName,
  destinationId,
  adults = 2,
  children = 'none',
  pages = 1,
  holidayType,
}: {
  originalSearch: string
  destinationName: string
  destinationId: string
  adults?: number
  children?: string
  pages?: number
  holidayType?: string
}) {
  const searchParams = new URLSearchParams(originalSearch)
  // Add destination name and ID
  if (holidayType) {
    searchParams.set('holidayTypes', holidayType)
  }
  searchParams.set('destinationName', destinationName)
  searchParams.set('destinationId', destinationId)
  searchParams.set('adults', String(adults))
  searchParams.set('children', children)
  searchParams.set('pages', String(pages))

  return searchParams.toString()
}

export function isMetadataExperimentOn(url: string, landingPageType: DestinationLandingPageType): boolean {
  if (config.BRAND === 'luxuryescapes' && landingPageType === DestinationLandingPageType.Resorts) {
    return url.length % 2 === 1
  }
  return false
}
